import { Button, TextField, Typography } from "@material-ui/core";
import React, { useEffect, useState } from "react";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "../../../../../components/Dialog";
import { actorName, DebtorBlockEvent, formatEventTime, isBlank } from "./debtorBlock";

interface Props {
  open: boolean;
  count: number;
  clientName: string;
  latestBlockEvent: DebtorBlockEvent | null;
  pending: boolean;
  error: string;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

const UnblockAllDialog = ({
  open,
  count,
  clientName,
  latestBlockEvent,
  pending,
  error,
  onCancel,
  onConfirm
}: Props): JSX.Element => {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setReason("");
    }
  }, [open]);

  const reasonBlank = isBlank(reason);

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={onCancel}>
      <DialogTitle onClose={onCancel}>Unblock factoring on all debtors?</DialogTitle>
      <DialogContent>
        <Typography paragraph>
          This turns off <strong>Block</strong> for all <strong>{count}</strong> debtors
          connected to <strong>{clientName}</strong>, and clears the block on debtors
          connected later.
        </Typography>
        {latestBlockEvent && (
          <Typography
            component="div"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              border: "1px solid #f1c7c7",
              background: "#fdecec",
              borderRadius: 4,
              padding: "10px 12px",
              marginBottom: 12
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: "uppercase",
                color: "#8e1414"
              }}
            >
              Why these debtors were blocked
            </div>
            <div style={{ fontSize: 14, color: "#212121" }}>{latestBlockEvent.reason}</div>
            <div style={{ fontSize: 12, color: "#5c2020" }}>
              Blocked by {actorName(latestBlockEvent)},{" "}
              {formatEventTime(latestBlockEvent.created)}
            </div>
          </Typography>
        )}
        <Typography
          component="div"
          style={{
            fontSize: 13,
            color: "#6d4c00",
            background: "#fff6e0",
            borderRadius: 4,
            padding: "10px 12px"
          }}
        >
          Debtors that were blocked one at a time before the bulk block will be unblocked
          too. Re-block them individually afterwards if needed.
        </Typography>
        <TextField
          label="Unblock reason (required, saved to the audit log)"
          placeholder="e.g. Insurance reinstated"
          value={reason}
          onChange={(event): void => setReason(event.target.value)}
          error={reasonBlank}
          helperText={reasonBlank ? "Enter a reason to unblock these debtors." : " "}
          required
          fullWidth
          margin="normal"
          inputProps={{ "aria-label": "Unblock reason" }}
        />
        {error && (
          <Typography color="error" role="alert">
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={reasonBlank || pending}
          onClick={(): void => onConfirm(reason.trim())}
        >
          {pending ? "Unblocking..." : `Unblock ${count} debtors`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnblockAllDialog;
